import { Link } from "@tanstack/react-router";
import { CTABand } from "./CTABand";
import { PhoneButton } from "./PhoneButton";
import { FAQ } from "./FAQ";
import { PreferredSourceButton } from "./PreferredSourceButton";
import { POST_BY_SLUG, RELATED_LINKS } from "@/lib/blog";
import { SERVICE_BY_SLUG } from "@/lib/services";
import type { BlogPost } from "@/lib/blog";

export function BlogPostPage({ post }: { post: BlogPost }) {
  const dateFormatted = new Date(post.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  return (
    <>
      <nav aria-label="Breadcrumb" className="border-b border-border bg-muted/30">
        <ol className="mx-auto flex max-w-4xl flex-wrap items-center gap-2 px-4 py-2 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li aria-hidden>›</li>
          <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
          <li aria-hidden>›</li>
          <li aria-current="page" className="text-secondary font-semibold">{post.title}</li>
        </ol>
      </nav>

      <section className="relative bg-secondary text-secondary-foreground overflow-hidden">
        <div className="relative mx-auto max-w-4xl px-4 py-14">
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
            {post.title}
          </h1>
          <p className="mt-4 text-secondary-foreground/85 text-lg">{post.description}</p>
          <p className="mt-3 text-sm text-secondary-foreground/70">By Phoenix Plumbing Co. · Updated {dateFormatted}</p>
          <div className="mt-6"><PhoneButton size="lg" /></div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12">
        <PreferredSourceButton className="mb-8" />
        {post.image && (
          <img decoding="async"
            src={post.image}
            alt={post.imageAlt || post.title}
            width={1600}
            height={912}
            loading="lazy"
            className="mb-8 w-full rounded-xl border border-border object-cover"
          />
        )}
        {post.contentHtml ? (
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: post.contentHtml }}
          />
        ) : (
          <div className="blog-content">
            {post.body.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
            ))}
          </div>
        )}

        {post.faqs && post.faqs.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-secondary mb-4">
              Frequently Asked Questions
            </h2>
            <FAQ items={post.faqs} />
          </section>
        )}

        <RelatedLinks slug={post.slug} />

        <div className="mt-12 rounded-xl border border-border bg-card p-6">
          <p className="text-foreground">
            Have a plumbing question this article didn't answer? Call Phoenix Plumbing Co. — a live Phoenix dispatcher picks up, and we'll give you straight advice on the phone.
          </p>
          <div className="mt-4"><PhoneButton size="lg" /></div>
        </div>
      </article>

      <CTABand />
    </>
  );
}
function RelatedLinks({ slug }: { slug: string }) {
  const rel = RELATED_LINKS[slug];
  if (!rel) return null;
  const services = rel.services
    .map((s) => SERVICE_BY_SLUG[s])
    .filter(Boolean);
  const posts = rel.posts
    .map((p) => POST_BY_SLUG[p])
    .filter(Boolean);
  return (
    <section className="mt-12 rounded-xl border border-border bg-muted/30 p-6">
      <h2 className="text-xl font-extrabold text-secondary">Related services</h2>
      <ul className="mt-3 space-y-2">
        {services.map((s) => (
          <li key={s!.slug}>
            <Link
              to={`/${s!.slug}` as any}
              className="text-accent-ink font-semibold hover:underline"
            >
              {s!.name} in Phoenix, AZ
            </Link>
          </li>
        ))}
      </ul>
      {posts.length > 0 && (
        <>
          <h3 className="mt-6 text-base font-extrabold text-secondary">Keep reading</h3>
          <ul className="mt-2 space-y-2">
            {posts.map((p) => (
              <li key={p!.slug}>
                <Link
                  to={`/${p!.slug}` as any}
                  className="text-accent-ink font-semibold hover:underline"
                >
                  {p!.title}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
